/* ════════════════════════════════════════════════════════════════════════
   WEEKLY REPORT UTILS — gentle, supportive summaries for the
   household. Pure functions over local state. Empty-state safe.
   ════════════════════════════════════════════════════════════════════════ */

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoDay(d) { return d.toISOString().slice(0, 10); }

/* ── Week range (Mon → Sun, anchored on a given date) ───────────────── */

export function getWeekRange(reference = new Date()) {
  const start = new Date(reference);
  start.setHours(0, 0, 0, 0);
  const dayIdx = (start.getDay() + 6) % 7; // Mon=0
  start.setDate(start.getDate() - dayIdx);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push({ iso: isoDay(d), label: DAY_LABELS[i], dayNumber: d.getDate(), date: d });
  }
  const fmt = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return {
    start, end, days,
    label: `${fmt(start)} – ${fmt(end)}`,
    startIso: isoDay(start), endIsoInclusive: isoDay(end),
  };
}

function withinWeek(iso, week) {
  return typeof iso === "string" && iso >= week.startIso && iso <= week.endIsoInclusive;
}

function withinWeekTs(ts, week) {
  if (!ts) return false;
  const d = new Date(ts);
  return d >= week.start && d <= week.end;
}

/* ── Mood trends ──────────────────────────────────────────────────── */

export function getMoodZoneCounts(state, week = getWeekRange()) {
  const out = { yellow: 0, green: 0, blue: 0, red: 0 };
  (state?.moodCheckins || []).forEach(c => {
    if (!withinWeekTs(c.createdAt, week)) return;
    if (out[c.zone] != null) out[c.zone] += 1;
  });
  return out;
}

export function getMoodTrendSummary(state, week = getWeekRange()) {
  const counts = getMoodZoneCounts(state, week);
  const total = counts.yellow + counts.green + counts.blue + counts.red;

  // Per-day aggregation for hardest/best day detection
  const dayAgg = {};
  week.days.forEach(d => { dayAgg[d.iso] = { yellow: 0, green: 0, blue: 0, red: 0, total: 0, label: d.label }; });
  (state?.moodCheckins || []).forEach(c => {
    const iso = (c.createdAt || "").slice(0, 10);
    if (!dayAgg[iso] || !c.zone) return;
    dayAgg[iso][c.zone] = (dayAgg[iso][c.zone] || 0) + 1;
    dayAgg[iso].total += 1;
  });

  let hardestDay = null;
  let bestDay = null;
  Object.entries(dayAgg).forEach(([iso, a]) => {
    if (a.total === 0) return;
    const hardness = a.red * 2 + a.blue;
    const ease = a.yellow + a.green * 1.5;
    if (hardestDay == null || hardness > hardestDay.score) hardestDay = { iso, label: a.label, score: hardness };
    if (bestDay == null || ease > bestDay.score) bestDay = { iso, label: a.label, score: ease };
  });
  if (hardestDay && hardestDay.score === 0) hardestDay = null;
  if (bestDay && bestDay.score === 0) bestDay = null;

  // Most common zone
  let mostCommonZone = null;
  Object.entries(counts).forEach(([zone, count]) => {
    if (!mostCommonZone || count > mostCommonZone.count) mostCommonZone = { zone, count };
  });
  if (!mostCommonZone || mostCommonZone.count === 0) mostCommonZone = null;

  return { total, counts, dayAgg, hardestDay, bestDay, mostCommonZone };
}

/* ── Per-member mood summary ──────────────────────────────────────── */

export function getMemberMoodSummary(state, week = getWeekRange()) {
  const members = state?.members || [];
  return members
    .filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet")
    .map(m => {
      const own = (state?.moodCheckins || []).filter(c => c.memberId === m.id && withinWeekTs(c.createdAt, week));
      const zoneCounts = own.reduce((acc, c) => { acc[c.zone] = (acc[c.zone] || 0) + 1; return acc; }, {});
      const top = Object.entries(zoneCounts).sort((a, b) => b[1] - a[1])[0];
      const moodCounts = own.reduce((acc, c) => { acc[c.moodLabel] = (acc[c.moodLabel] || 0) + 1; return acc; }, {});
      const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0];
      const energies = own.map(c => c.energy).filter(Boolean);
      const energyPattern = energies.length
        ? energies.reduce((acc, e) => { acc[e] = (acc[e] || 0) + 1; return acc; }, {})
        : null;

      let note = `${m.name} hasn't checked in this week yet. ARA will start trends once they do.`;
      if (own.length > 0) {
        const baseMood = topMood ? topMood[0] : "calm";
        const zoneNote =
          top?.[0] === "red"    ? "Some big-feeling days. A gentler load may help." :
          top?.[0] === "blue"   ? "Low-energy patches. Short, easy tasks suit best." :
          top?.[0] === "yellow" ? "Plenty of positive energy. Lean into shared time." :
          "Mostly steady. Keep the rhythm going.";
        note = `${m.name} checked in ${own.length} time${own.length === 1 ? "" : "s"}. Mostly ${baseMood.toLowerCase()}. ${zoneNote}`;
      }

      return {
        member: m,
        checkinCount: own.length,
        zoneCounts,
        topZone: top ? top[0] : null,
        topMood: topMood ? { label: topMood[0], count: topMood[1] } : null,
        energyPattern,
        note,
      };
    });
}

/* ── Cleaning summary ─────────────────────────────────────────────── */

export function getCleaningSummary(state, week = getWeekRange()) {
  const all = state?.cleaningTasks || [];
  const open = all.filter(t => t.status !== "Done");
  const completed = all.filter(t => t.status === "Done");

  const roomCounts = {};
  open.forEach(t => { if (t.room) roomCounts[t.room] = (roomCounts[t.room] || 0) + 1; });
  const heaviestRoom = Object.entries(roomCounts).sort((a, b) => b[1] - a[1])[0] || null;

  const roomsResetThisWeek = (state?.rooms || []).filter(r => withinWeek(r.lastCleaned, week));

  let recommendation = "Daily reset only — keep it light and predictable.";
  if (heaviestRoom) {
    recommendation = `ARA recommends focusing on the ${String(heaviestRoom[0]).replace(/-/g, " ")} next week.`;
  }

  return {
    totalRooms: (state?.rooms || []).length,
    openCount: open.length,
    completedCount: completed.length,
    heaviestRoom: heaviestRoom ? { id: heaviestRoom[0], count: heaviestRoom[1] } : null,
    roomsResetThisWeek,
    recommendation,
  };
}

/* ── Chore summary ────────────────────────────────────────────────── */

export function getChoreSummary(state, week = getWeekRange()) {
  const tasks = state?.tasks || [];
  const choreTasks = tasks.filter(t => t.category === "Chores");
  const completed = choreTasks.filter(t => t.status === "Done");
  const open = choreTasks.filter(t => t.status !== "Done" && t.status !== "Paused");
  const allCompleted = tasks.filter(t => t.status === "Done" && withinWeek(t.dueDate, week));

  let overdue = null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayIso = isoDay(today);
  const overdueTasks = open.filter(t => t.dueDate && t.dueDate < todayIso).sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""));
  if (overdueTasks.length) overdue = overdueTasks[0];

  return {
    choresCompleted: completed.length,
    choresOpen: open.length,
    totalCompletedThisWeek: allCompleted.length,
    overdue,
  };
}

/* ── Swap summary ─────────────────────────────────────────────────── */

export function getSwapSummary(state, week = getWeekRange()) {
  const members = state?.members || [];
  const findName = (id) => {
    const m = members.find(x => x.id === id);
    return m?.name || (id === "ara" ? "ARA" : id === "all" ? "Everyone" : id || "Someone");
  };
  const swaps = [
    ...(state?.tasks || []).map(t => ({ ...t, _kind: "task" })),
    ...(state?.cleaningTasks || []).map(t => ({ ...t, _kind: "cleaning" })),
  ].filter(t => t.swappedAt && withinWeekTs(t.swappedAt, week));

  swaps.sort((a, b) => (b.swappedAt || "").localeCompare(a.swappedAt || ""));

  const moments = swaps.slice(0, 3).map(t => ({
    id: `${t._kind}-${t.id}`,
    title: t.title,
    takerName: findName(t.swappedTo || t.assignedTo),
    giverName: findName(t.previousAssignedTo),
    when: t.swappedAt,
    reason: t.swapReason || "",
    kind: t._kind,
  }));

  // Helped count per taker
  const helperCounts = {};
  swaps.forEach(t => {
    const id = t.swappedTo || t.assignedTo;
    if (!id) return;
    helperCounts[id] = (helperCounts[id] || 0) + 1;
  });
  const helpers = Object.entries(helperCounts)
    .map(([id, count]) => ({ id, name: findName(id), count }))
    .sort((a, b) => b.count - a.count);

  let summary = "No swaps recorded yet. Help-out moments will appear here when someone takes a task for another person.";
  if (swaps.length === 1) summary = "1 helpful swap this week. A small act of teamwork makes a difference.";
  else if (swaps.length > 1) summary = `${swaps.length} helpful swaps this week. The load was shared more kindly on busy days.`;

  return { count: swaps.length, moments, helpers, summary };
}

/* ── Load balance per person ──────────────────────────────────────── */

export function getLoadBalanceSummary(state, week = getWeekRange()) {
  const members = (state?.members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet");
  const tasks = state?.tasks || [];
  const cleaningTasks = state?.cleaningTasks || [];
  const swaps = [...tasks, ...cleaningTasks].filter(t => t.swappedAt && withinWeekTs(t.swappedAt, week));

  const rows = members.map(m => {
    const assignedTasks  = tasks.filter(t => t.assignedTo === m.id && t.status !== "Paused").length;
    const assignedClean  = cleaningTasks.filter(t => t.assignedTo === m.id).length;
    const completedTasks = tasks.filter(t => t.assignedTo === m.id && t.status === "Done").length;
    const completedClean = cleaningTasks.filter(t => t.assignedTo === m.id && t.status === "Done").length;
    const totalAssigned  = assignedTasks + assignedClean;
    const totalCompleted = completedTasks + completedClean;
    const takenSwaps     = swaps.filter(t => (t.swappedTo || t.assignedTo) === m.id).length;
    const gaveSwaps      = swaps.filter(t => t.previousAssignedTo === m.id).length;

    let level = "Balanced", levelTone = "amber", note = "Good balance.";
    if (totalAssigned >= 9)       { level = "Heavy";   levelTone = "rose";    note = "Carried a lot this week — may need support."; }
    else if (totalAssigned >= 6)  { level = "Active";  levelTone = "violet";  note = "Plenty on this week."; }
    else if (totalAssigned >= 3)  { level = "Balanced";levelTone = "amber";   note = "Good balance."; }
    else                          { level = "Light";   levelTone = "emerald"; note = "Light load."; }

    return {
      member: m,
      assigned: totalAssigned,
      completed: totalCompleted,
      takenSwaps,
      gaveSwaps,
      level, levelTone, note,
    };
  });

  return { rows };
}

/* ── ARA insights for the week ────────────────────────────────────── */

export function getWeeklyAraInsights(state, week = getWeekRange()) {
  const mood = getMoodTrendSummary(state, week);
  const cleaning = getCleaningSummary(state, week);
  const swap = getSwapSummary(state, week);
  const load = getLoadBalanceSummary(state, week);

  const items = [];
  if (mood.hardestDay) {
    items.push({ id: "hardest-day", text: `${mood.hardestDay.label} had the most challenging mood check-ins. Plan a softer day next week.` });
  }
  if (mood.bestDay && mood.bestDay.label !== mood.hardestDay?.label) {
    items.push({ id: "best-day", text: `${mood.bestDay.label} felt the most settled. Worth repeating what worked.` });
  }
  const heavy = load.rows.filter(r => r.level === "Heavy");
  if (heavy.length) {
    items.push({ id: "heavy-load", text: `${heavy.map(h => h.member.name).join(" & ")} carried more this week. Consider redistributing tasks.` });
  }
  if (swap.count > 0) {
    items.push({ id: "swaps-helped", text: `${swap.count} swap${swap.count === 1 ? "" : "s"} this week helped reduce overload on busy days.` });
  }
  if (cleaning.openCount > 10) {
    items.push({ id: "cleaning-backlog", text: `${cleaning.openCount} cleaning tasks are open. A shorter Daily Reset may feel more sustainable.` });
  }
  if (cleaning.heaviestRoom) {
    const roomLabel = String(cleaning.heaviestRoom.id).replace(/-/g, " ");
    items.push({ id: "room-focus", text: `The ${roomLabel} has the most open jobs. Start there next week.` });
  }
  if (mood.total === 0) {
    items.push({ id: "no-mood", text: "No mood check-ins this week. ARA will surface trends once the family starts checking in." });
  }
  if (items.length === 0) {
    items.push({ id: "calm-week", text: "This week looked steady. Keep the rhythm." });
  }
  return items.slice(0, 5);
}

/* ── Suggested focus for next week (3 cards) ──────────────────────── */

export function getSuggestedNextWeekFocus(state, week = getWeekRange()) {
  const mood = getMoodTrendSummary(state, week);
  const cleaning = getCleaningSummary(state, week);
  const load = getLoadBalanceSummary(state, week);

  const rhythm = {
    id: "rhythm",
    tone: "amber",
    title: "Household rhythm",
    text: load.rows.some(r => r.level === "Heavy")
      ? "Redistribute one or two recurring tasks so no one carries the week alone."
      : "Prep lunches the night before school days — small steps lower morning stress.",
  };

  const cleaningFocus = {
    id: "cleaning",
    tone: "cyan",
    title: "Cleaning focus",
    text: cleaning.heaviestRoom
      ? `Focus the weekly clean on the ${String(cleaning.heaviestRoom.id).replace(/-/g, " ")}.`
      : "Keep cleaning to Daily Reset only on high-stress days.",
  };

  const wellbeing = {
    id: "wellbeing",
    tone: "rose",
    title: "Family wellbeing",
    text: mood.total === 0
      ? "Try one shared mood check-in after dinner — a calm way to start the habit."
      : mood.counts.red + mood.counts.blue > mood.counts.yellow + mood.counts.green
        ? "Add a short Calm Mode evening this week. A slower pace helps everyone reset."
        : "Encourage mood check-ins after school — when feelings change the fastest.",
  };

  return [rhythm, cleaningFocus, wellbeing];
}
